import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed } from '@angular/core';
import { SWELL_OPTIONS } from '@navstation-portal/reporting/data-access/vessel-report';
import {
  AnyOffshoreSectionState,
  OffshorePositionAndWeatherState,
} from '@navstation-portal/reporting/data-model/offshore-report';
import { INIT_MODES, InitMode } from '@navstation-portal/reporting/data-model/vessel-report';
import { LatLngModel, SelectOption } from '@navstation-portal/shared/data-model/common';
import { signalStore, withComputed, withState } from '@ngrx/signals';
import { buildFormSection, initStateField } from '../../../offshore-report-utils/store.utility';
import { createDynamicSectionState } from '../../offshore-vm-builder-helpers';
import { withOffshoreFormStoreReducer } from '../../with-offshore-form-store-reducer';
import { buildOffshorePositionAndWeatherVm } from './offshore-position-and-weather-vm-builders';
import { withOffshorePositionAndWeatherEffects } from './with-offshore-position-and-weather-effects';
import { offshorePositionAndWeatherEvents } from './with-offshore-position-and-weather-events';

const createEmptyLatLng = () => ({
  latDegrees: undefined,
  latMinutes: undefined,
  latSeconds: undefined,
  latDirection: undefined,
  longDegrees: undefined,
  longMinutes: undefined,
  longSeconds: undefined,
  longDirection: undefined,
});

const getSwellOption = (code: number | undefined) => {
  return SWELL_OPTIONS.find((option) => option.code === code);
};

function buildState(data?: OffshorePositionAndWeatherState) {
  return {
    positionAndLogistics: {
      position: initStateField<LatLngModel | undefined>(
        data?.shipPosition?.position
          ? {
              latDegrees: data.shipPosition.position.latDegrees,
              latMinutes: data.shipPosition.position.latMinutes,
              latSeconds: data.shipPosition.position.latSeconds,
              latDirection: data.shipPosition.position.latDirection,
              longDegrees: data.shipPosition.position.longDegrees,
              longMinutes: data.shipPosition.position.longMinutes,
              longSeconds: data.shipPosition.position.longSeconds,
              longDirection: data.shipPosition.position.longDirection,
            }
          : createEmptyLatLng()
      ),
      logDistance: initStateField<number>(data?.shipPosition?.logDistance),
      observedDistance: initStateField<number>(data?.shipPosition?.observedDistance),
      logSpeed: initStateField<number>(data?.shipPosition?.logSpeed),
      observedSpeed: initStateField<number>(data?.shipPosition?.observedSpeed),
      courseOverGround: initStateField<number>(data?.shipPosition?.courseOverGround),
    },
    wind: buildFormSection(data?.wind),
    observation: {
      observationTime: initStateField<Date | undefined>(data?.observation?.observationTime),
      observationTimezone: initStateField<SelectOption>(undefined),
    },
    speedAndDistance: buildFormSection(data?.speedAndDistance),
    shipPosition: {
      position: initStateField<LatLngModel | undefined>(
        data?.shipPosition?.position
          ? {
              latDegrees: data.shipPosition.position.latDegrees,
              latMinutes: data.shipPosition.position.latMinutes,
              latSeconds: data.shipPosition.position.latSeconds,
              latDirection: data.shipPosition.position.latDirection,
              longDegrees: data.shipPosition.position.longDegrees,
              longMinutes: data.shipPosition.position.longMinutes,
              longSeconds: data.shipPosition.position.longSeconds,
              longDirection: data.shipPosition.position.longDirection,
            }
          : createEmptyLatLng()
      ),
    },
    currentsAndSeaWater: {
      seaSwell: initStateField<SelectOption | undefined>(getSwellOption(data?.currentsAndSeaWater?.seaSwell)),
      waveHeight: initStateField<number>(data?.currentsAndSeaWater?.waveHeight),
      waterDepth: initStateField<number>(data?.currentsAndSeaWater?.waterDepth),
      currentDirectionTrueNorth: initStateField<number>(data?.currentsAndSeaWater?.currentDirectionTrueNorth),
      trueCurrentSpeed: initStateField<number>(data?.currentsAndSeaWater?.trueCurrentSpeed),
      apparentCurrentDirection: initStateField<number>(data?.currentsAndSeaWater?.apparentCurrentDirection),
      apparentCurrentSpeed: initStateField<number>(data?.currentsAndSeaWater?.apparentCurrentSpeed),
      apparentWaveDirection: initStateField<number>(data?.currentsAndSeaWater?.apparentWaveDirection),
      seaTemperature: initStateField<number>(data?.currentsAndSeaWater?.seaTemperature),
      seaWaterDensity: initStateField<number>(data?.currentsAndSeaWater?.seaWaterDensity),
      atmosphericPressure: initStateField<number>(data?.currentsAndSeaWater?.atmosphericPressure),
      salinity: initStateField<number>(data?.currentsAndSeaWater?.salinity),
    },
    dynamicSection: createDynamicSectionState(
      []
      // this.OffshoreReportsStore.formConfig()?.tabs?.['positionAndWeather']?.customFields ?? [],
    ),
  };
}

const initBlank = () => buildState();

const initWithCarryOver = (data: OffshorePositionAndWeatherState) => {
  return buildState(data);
};

function initWithExistingReport(data: OffshorePositionAndWeatherState) {
  return buildState(data);
}

const initOffshorePositionAndWeatherStateReducer = (initialData: AnyOffshoreSectionState, initMode: InitMode) => {
  const data = initialData as OffshorePositionAndWeatherState;
  switch (initMode) {
    case INIT_MODES.CARRY_OVER:
      return initWithCarryOver(data);
    case INIT_MODES.IMPORT:
      return initWithExistingReport(data);
    case INIT_MODES.BLANK:
      return initBlank();
    default:
      return initBlank();
  }
};

export const OffshorePositionAndWeatherFormStore = signalStore(
  { providedIn: 'root' },
  withDevtools('OffshorePositionAndWeatherFormStore'),
  withState(initBlank()),
  withOffshorePositionAndWeatherEffects(),
  withOffshoreFormStoreReducer(offshorePositionAndWeatherEvents, initOffshorePositionAndWeatherStateReducer, initBlank),
  withComputed((store) => ({
    vm: computed(() => {
      return buildOffshorePositionAndWeatherVm(store);
    }),
  }))
);
